import { durableGet, durableRemove, durableSet } from "./binding";

/**
 * Where a diagnostic capture (screenshot / log) had got to, kept somewhere that
 * survives the page dying.
 *
 * Why this exists (2026-09-28, Hasselbacken QB24C): every Samsung screenshot
 * ever requested reached "acked" with `result: null` - 0 images in 7 attempts.
 * The command was marked executed up front, the capture never finished, the
 * page came back up ~30-80 s later, drained the still-open command, found it in
 * the executed-set and acked it BLANK as a duplicate. So the console showed a
 * success with nothing attached, or - once its 24 s poll had given up - "sent"
 * forever. The device's own logs show the signage machine re-entering
 * `Initializing` in the same second as the blank ack: the page restarted
 * mid-capture, and nothing recorded where.
 *
 * With this trail, the boot that finds a capture it never finished acks it with
 * an error naming the stage that was running when the page went down, so the
 * console says "page restarted during tizen-read" rather than nothing.
 *
 * The in-memory set answers a different question: "is this capture still
 * running on THIS page?" A re-delivered command (server sweep, SSE reconnect)
 * must neither re-run nor ack a capture that is still working - its upload is
 * the ack.
 */

const KEY = "foyer.captureInFlight";

interface Crumb {
  commandId: string;
  stage: string;
  at: number;
}

const running = new Set<string>();

function read(): Crumb | null {
  try {
    const raw = durableGet(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Partial<Crumb>;
    return typeof c.commandId === "string" && typeof c.stage === "string"
      ? { commandId: c.commandId, stage: c.stage, at: Number(c.at) || 0 }
      : null;
  } catch {
    return null;
  }
}

/** Record that `commandId` is now in `stage`. Survives a reload. */
export function captureStage(commandId: string, stage: string): void {
  running.add(commandId);
  try {
    durableSet(KEY, JSON.stringify({ commandId, stage, at: Date.now() }));
  } catch {
    // No durable store: we lose the stage name, not the capture.
  }
}

/** The capture finished (uploaded, or acked with its own error). */
export function captureDone(commandId: string): void {
  running.delete(commandId);
  try {
    if (read()?.commandId === commandId) durableRemove(KEY);
  } catch {
    // ignore
  }
}

/** Is this capture still running on the current page? */
export function captureRunning(commandId: string): boolean {
  return running.has(commandId);
}

/**
 * The stage a PREVIOUS page was in when it died with this capture open, or
 * null when no trail names it. Consumes the trail.
 */
export function takeInterruptedStage(commandId: string): string | null {
  const c = read();
  if (!c || c.commandId !== commandId) return null;
  try {
    durableRemove(KEY);
  } catch {
    // ignore
  }
  return c.stage;
}

export const __testing = {
  reset(): void {
    running.clear();
    try {
      durableRemove(KEY);
    } catch {
      // ignore
    }
  },
};
